import { useEffect, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import {
    Badge,
    Button,
    ButtonGroup,
    SearchInput,
    Separator,
    Sheet,
    Skeleton,
    lucideIcons,
    toast,
} from '@round/creatorbase-ui';
import { influencer } from '@round/api';
import uniqBy from 'lodash/uniqBy';
import { getCreatorName, PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
// Sort mapping lives with the add-to-campaign modal, which the Creator Explore page also reaches into.
import { mapSortToApiParams } from 'Modules/Plans/Project/ProjectDetails/components/AddCreatorsToCampaignModal/helpers';
import { useCreatorExplore } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplore';
import { useCreatorExplorePlatformSelection } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplorePlatformSelection';
import { FilterSidebar } from 'Modules/Plans/CreatorSearch/components/FilterSidebar/FilterSidebar';
import {
    Filters,
    getActiveFilterCount,
    getFiltersFromUrlState,
    mapFiltersToApiParams,
} from 'Modules/Plans/CreatorSearch/utils/filters';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';
import { usePoolDetail } from '../../hooks/usePoolDetail';
import { getCreatorSelectionId, getCreatorSelectionIds } from './helpers';
import { useAddCreatorsToPool } from './useAddCreatorsToPool';
import { useCreatorSelection } from './useCreatorSelection';
import { AddCreatorsTable } from './components/AddCreatorsTable/AddCreatorsTable';
import { SelectedCreators } from './components/SelectedCreators/SelectedCreators';
import { Meta } from './components/AddCreatorsTable/helpers';
import styles from './AddCreatorsDrawer.module.css';

const { ArrowLeft, Eye, SlidersHorizontal, Trash2 } = lucideIcons;

const SEARCH_DEBOUNCE_MS = 700;

type ViewMode = 'browse' | 'selected';

// All-empty filter state, derived from the same mapper the Creator Explore page uses.
const EMPTY_FILTERS: Filters = getFiltersFromUrlState({});

type Props = {
    isOpen: boolean;
    poolId: number | null | undefined;
    onClose: () => void;
};

export const AddCreatorsDrawer = ({ isOpen, poolId, onClose }: Props) => {
    const { selectedClientId } = useClientSelect();
    // Already fetched by the pool info header, so the name is normally in cache by the time this opens.
    const { pool } = usePoolDetail(poolId);

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [areFiltersShown, setAreFiltersShown] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [viewMode, setViewMode] = useState<ViewMode>('browse');

    // The selected platform only decides which account's stats a row shows. Adding a creator adds
    // every platform account they have, so it never changes what ends up in the pool.
    const { getRowSelectedPlatform, onRowPlatformSelect, resetSelectedPlatforms } =
        useCreatorExplorePlatformSelection();

    const {
        data,
        count,
        isLoading,
        hasError,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        getCreatorImages,
        getAreCreatorImagesLoading,
    } = useCreatorExplore(
        {
            page_size: PAGE_SIZE,
            client_id: selectedClientId,
            search,
            ...mapSortToApiParams(sorting),
            ...mapFiltersToApiParams(filters),
        },
        { enabled: isOpen && isNumber(selectedClientId) }
    );

    const activeFilterCount = getActiveFilterCount(filters);

    const { addCreators, fetchCreatorsByIds, getPoolAccounts, resetAddedAccounts } = useAddCreatorsToPool(poolId);

    const { selectedCreatorIds, toggleCreator, selectCreators, deselectCreators, clearSelection } =
        useCreatorSelection();
    const [isBulkAdding, setIsBulkAdding] = useState(false);

    // Cleared on close rather than on open: resetting after opening would let the first render, and the
    // query it kicks off, still use the previous session's search and filters. Adds are cleared with
    // them, so a reopen reads membership from the refetched rows rather than from a past session.
    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setFilters(EMPTY_FILTERS);
            setAreFiltersShown(false);
            setSorting([]);
            setViewMode('browse');
            resetSelectedPlatforms();
            resetAddedAccounts();
            clearSelection();
        }
    }, [isOpen, resetSelectedPlatforms, resetAddedAccounts, clearSelection]);

    useEffect(() => {
        if (viewMode === 'selected' && !selectedCreatorIds.size) {
            setViewMode('browse');
        }
    }, [viewMode, selectedCreatorIds]);

    // The drawer stays open so several creators can be added in one session, matching AddToCampaign.
    const handleCreatorAdd = async (creator: influencer.CreatorSearchResult) => {
        const { failedCreators } = await addCreators([creator]);

        if (failedCreators.length) {
            toast.error(`Could not add ${getCreatorName(creator)} to the pool`);
            return;
        }

        // Nothing is left for the footer to add for this creator, so drop them rather than leave a count
        // that promises more than it will do.
        deselectCreators([creator]);
        toast.success(`Added ${getCreatorName(creator)} to the pool`);
    };

    const handleSelectedCreatorsAdd = async () => {
        if (!selectedCreatorIds.size) {
            return;
        }

        setIsBulkAdding(true);

        try {
            // The accounts to post live on the records, and the loaded rows likely cover at least some of
            // a selection, so only the ids they don't are fetched back.
            const loadedCreators = data.filter((creator) => getIsCreatorSelected(creator));
            const loadedCreatorIds = new Set(getCreatorSelectionIds(loadedCreators));
            const missingCreatorIds = [...selectedCreatorIds].filter((creatorId) => !loadedCreatorIds.has(creatorId));
            const missingCreators = missingCreatorIds.length ? await fetchCreatorsByIds(missingCreatorIds) : [];
            // Deduped because paging can surface one creator on more than one page, and a repeat would
            // post its accounts twice and overstate what the add reports.
            const selectedCreators = uniqBy([...loadedCreators, ...missingCreators], getCreatorSelectionId);

            const creatorsToAdd = selectedCreators.filter((creator) => getPoolAccounts(creator).accountIdsToAdd.length);

            if (!creatorsToAdd.length) {
                // Every selected creator is already a member, or no longer exists, so there is nothing for
                // a retry to act on and the whole selection goes.
                clearSelection();
                toast.info('Those creators are already in the pool');
                return;
            }

            const { failedCreators } = await addCreators(creatorsToAdd);

            // Drops whoever landed, along with anyone who turned out to have nothing left to add. A
            // creator whose accounts didn't all land stays, so a retry is one click away. The failures
            // are the very records that went in, so they match by identity.
            const failedCreatorSet = new Set(failedCreators);
            deselectCreators(selectedCreators.filter((creator) => !failedCreatorSet.has(creator)));

            const addedCount = creatorsToAdd.length - failedCreators.length;

            if (!failedCreators.length) {
                toast.success(`Added ${pluralizeCreators(addedCount)} to the pool`);
                return;
            }

            if (!addedCount) {
                toast.error(`Could not add ${pluralizeCreators(failedCreators.length)} to the pool`);
                return;
            }

            toast.error(`Added ${addedCount} of ${creatorsToAdd.length} creators. ${failedCreators.length} failed`);
        } catch {
            // Only the fetch throws, and without it there is nothing to post for anyone.
            toast.error('Could not add the selected creators to the pool');
        } finally {
            // Cleared however the add settles, so an unexpected throw can't leave the button spinning for
            // the rest of the session.
            setIsBulkAdding(false);
        }
    };

    const getIsCreatorSelected = (creator: influencer.CreatorSearchResult) => {
        const creatorId = getCreatorSelectionId(creator);
        return Boolean(creatorId && selectedCreatorIds.has(creatorId));
    };

    const getIsCreatorSelectable = (creator: influencer.CreatorSearchResult) =>
        Boolean(getCreatorSelectionId(creator)) && getPoolAccounts(creator).accountIdsToAdd.length > 0;

    const meta: Meta = {
        isLoading,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
        getRowSelectedPlatform,
        onRowPlatformSelect,
        getCreatorPoolAccounts: getPoolAccounts,
        poolId,
        getIsCreatorSelected,
        getIsCreatorSelectable,
        onCreatorAdd: handleCreatorAdd,
    };

    const isSelectedView = viewMode === 'selected';

    return (
        <Sheet
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <Sheet.Content side="bottom" className={styles.sheet}>
                <Sheet.Header className={styles.header}>
                    <Sheet.Title>{pool ? `Add creators to ${pool.name}` : 'Add creators to pool'}</Sheet.Title>
                    <Sheet.Description>Browse creators and add them to this pool.</Sheet.Description>
                </Sheet.Header>

                {/* Search, filter and sort state is left untouched, so going back restores the browse view. */}
                <div className={styles.toolbar}>
                    {isSelectedView ? (
                        <>
                            <Button variant="outline" size="xs" onClick={() => setViewMode('browse')}>
                                <ArrowLeft className={styles.buttonIcon} />
                                Back to search
                            </Button>
                            <span className={styles.countLabel}>
                                {pluralizeCreators(selectedCreatorIds.size)} selected
                            </span>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" size="xs" onClick={() => setAreFiltersShown((shown) => !shown)}>
                                <SlidersHorizontal className={styles.buttonIcon} />
                                {areFiltersShown ? 'Hide Filters' : 'Show Filters'}
                                {!areFiltersShown && activeFilterCount > 0 ? (
                                    <Badge size="sm">{activeFilterCount}</Badge>
                                ) : null}
                            </Button>
                            <SearchInput
                                className={styles.search}
                                placeholder="Search creators..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                debounce={SEARCH_DEBOUNCE_MS}
                            />
                            <span className={styles.countLabel}>
                                {isLoading ? (
                                    <Skeleton className={styles.countSkeleton} />
                                ) : typeof count === 'number' ? (
                                    pluralizeCreators(count)
                                ) : null}
                            </span>
                        </>
                    )}
                </div>

                <div className={styles.contentRow}>
                    {areFiltersShown && !isSelectedView ? (
                        <aside className={styles.sidebar}>
                            <FilterSidebar filters={filters} onFiltersChange={setFilters} />
                        </aside>
                    ) : null}

                    <section className={styles.tableSection}>
                        {isSelectedView ? (
                            <SelectedCreators
                                selectedCreatorIds={selectedCreatorIds}
                                getCreatorPoolAccounts={getPoolAccounts}
                                getIsCreatorSelected={getIsCreatorSelected}
                                onCreatorRemove={(creator) => deselectCreators([creator])}
                                isRemoveDisabled={isBulkAdding}
                                className={styles.tableContainer}
                            />
                        ) : (
                            <AddCreatorsTable
                                data={data}
                                meta={meta}
                                sorting={sorting}
                                onSortingChange={setSorting}
                                onEndReached={() => {
                                    if (hasNextPage && !isFetchingNextPage) {
                                        void fetchNextPage();
                                    }
                                }}
                                onCreatorToggle={toggleCreator}
                                onSelectAll={selectCreators}
                                onDeselectAll={deselectCreators}
                                className={styles.tableContainer}
                            />
                        )}
                    </section>
                </div>

                <footer className={styles.footer}>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>

                    <div className={styles.footerActions}>
                        {selectedCreatorIds.size ? (
                            <>
                                <ButtonGroup>
                                    <ButtonGroup.Text>{selectedCreatorIds.size} Selected</ButtonGroup.Text>
                                    {isSelectedView ? null : (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setViewMode('selected')}
                                            disabled={isBulkAdding}
                                        >
                                            <Eye className={styles.buttonIcon} />
                                            View
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className={styles.clearButton}
                                        onClick={clearSelection}
                                        disabled={isBulkAdding}
                                    >
                                        <Trash2 className={styles.buttonIcon} />
                                        Clear
                                    </Button>
                                </ButtonGroup>

                                <Separator orientation="vertical" className={styles.footerSeparator} />
                            </>
                        ) : null}
                        {/* Counts the selection itself. Whether a creator still has an account to add is
                            settled by the fetch the add makes, not by rows the table may no longer hold. */}
                        <Button
                            size="sm"
                            onClick={handleSelectedCreatorsAdd}
                            isLoading={isBulkAdding}
                            disabled={isBulkAdding || !selectedCreatorIds.size}
                        >
                            Add {selectedCreatorIds.size ? pluralizeCreators(selectedCreatorIds.size) : 'creators'}
                        </Button>
                    </div>
                </footer>
            </Sheet.Content>
        </Sheet>
    );
};

const pluralizeCreators = (count: number): string => `${count} ${count === 1 ? 'creator' : 'creators'}`;
